import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';
import reviewsReducer from 'Guide/ProductReviews/reducer';
import {
   TEAM_MEMBERS_STATE_ROOT,
   teamMembersReducer,
} from 'Guide/Courses/store/team-members.state';
import {
   INDIVIDUAL_DETAILS_STATE_ROOT,
   individualDetailsReducer,
} from 'Guide/Courses/store/individual-details.state';
import { COMPLETIONS_STATE_ROOT, completionsReducer } from 'Guide/Courses/store/completions.state';
import headerReducer from 'Shared/Header/reducer';
import popupReducer from 'Shared/PopupModal/reducer';
import bannerReducer from 'Shared/Announcements/reducer';
import { selectedOptionSlice, internationalBuyBoxSlice } from 'Cart/shared-state';
import { modalIsOpenSlice } from 'Shared/Header/shared-state';
import { hideSubBoxSlice } from 'Shared/newsletter_shared_state';
import { selectedWikiDeviceSlice, savingState, inputFormValue } from 'Guide/wiki_search_state';

/* Define the Reducers that will always be present in the application.
 * We need at least one reducer initially, so 'global' will be an empty reducer
 * that we can potentially use in the future if need be.
 */
const staticReducers = {
   reviewsState: reviewsReducer,
   [TEAM_MEMBERS_STATE_ROOT]: teamMembersReducer,
   [INDIVIDUAL_DETAILS_STATE_ROOT]: individualDetailsReducer,
   [COMPLETIONS_STATE_ROOT]: completionsReducer,
   headerState: headerReducer,
   popupState: popupReducer,
   bannerState: bannerReducer,
   [selectedOptionSlice.name]: selectedOptionSlice.reducer,
   [internationalBuyBoxSlice.name]: internationalBuyBoxSlice.reducer,
   [modalIsOpenSlice.name]: modalIsOpenSlice.reducer,
   [hideSubBoxSlice.name]: hideSubBoxSlice.reducer,
   [selectedWikiDeviceSlice.name]: selectedWikiDeviceSlice.reducer,
   [savingState.name]: savingState.reducer,
   [inputFormValue.name]: inputFormValue.reducer,
};

export const store = configureStore({
   reducer: staticReducers,
   middleware: getDefaultMiddleware({
      // We're currently doing things we shouldn't be with our store; let's add some exemptions for now.
      immutableCheck: {
         ignoredPaths: [
            'reviewsState',
            TEAM_MEMBERS_STATE_ROOT,
            INDIVIDUAL_DETAILS_STATE_ROOT,
            COMPLETIONS_STATE_ROOT,
            'headerState',
            'popupState',
            'bannerState',
            selectedOptionSlice.name,
            internationalBuyBoxSlice.name,
            modalIsOpenSlice.name,
            hideSubBoxSlice.name,
         ],
      },
      serializableCheck: {
         ignoredPaths: [
            'reviewsState',
            TEAM_MEMBERS_STATE_ROOT,
            INDIVIDUAL_DETAILS_STATE_ROOT,
            COMPLETIONS_STATE_ROOT,
            'headerState',
            'popupState',
            'bannerState',
            selectedOptionSlice.name,
            internationalBuyBoxSlice.name,
            modalIsOpenSlice.name,
            hideSubBoxSlice.name,
         ],
         ignoredActionPaths: ['notifications', 'popup'],
      },
   }),
});

export function onSelectedWikiDeviceChange(listener: (e: null | string) => void) {
   return store.subscribe(() => {
      listener(selectedWikiDeviceSlice.selector(store.getState()));
   });
}

export function onSavingStateChange(listener: (e: boolean) => void) {
   return store.subscribe(() => {
      listener(savingState.selector(store.getState()));
   });
}

export const setSelectedOption = selectedOptionSlice.setter(store);
export const setInternationalBuyBox = internationalBuyBoxSlice.setter(store);
export const setModalIsOpen = modalIsOpenSlice.setter(store);
export const setHideSubBox = hideSubBoxSlice.setter(store);
export const setSelectedWikiDevice = selectedWikiDeviceSlice.setter(store);
export const getSelectedWikiDevice = () => selectedWikiDeviceSlice.selector(store.getState());
export const setSavingState = savingState.setter(store);
export const getSavingState = () => savingState.selector(store.getState());
export const setInputFormValue = inputFormValue.setter(store);
export const getInputFormValue = () => inputFormValue.selector(store.getState());

export type RootState = ReturnType<typeof store.getState>;
